import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OPERATORS_REQUIRING_TWO_VALUES } from "../services/utils";
import { QuerySpecMatch, QueryTermOp } from "../schema/types/query/query";
import { QueryInput, QueryInputTerm, QueryInputTermOp, getDefaultQueryInput } from "../schema/types/query/input";

export const queryInputSlice = createSlice({
  name: "queryInput",
  initialState: getDefaultQueryInput(),
  reducers: {
    setMatch: (state, action: PayloadAction<QuerySpecMatch>) => {
      state.match = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      if (action.payload === "") {
        state.sort.isEnabled = false;
      } else {
        state.sort.isEnabled = true;
        state.sort.by = action.payload;
      }
    },
    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      if (!state.sort.isEnabled) {
        return;
      }
      state.sort.order = action.payload;
    },
    addTerm: (state, action: PayloadAction<QueryInputTerm>) => {
      state.terms.push(action.payload);
    },
    removeTerm: (state, action: PayloadAction<string>) => {
      state.terms = state.terms.filter((term) => term.id !== action.payload);
    },
    updateTerm: (state, action: PayloadAction<{ id: string; updates: Partial<QueryInputTerm> }>) => {
      const { id, updates } = action.payload;
      const termIndex = state.terms.findIndex((term) => term.id === id);
      if (termIndex !== -1) {
        state.terms[termIndex] = { ...state.terms[termIndex], ...updates };
      }
    },
    updateTermKey: (state, action: PayloadAction<{ id: string; key: string }>) => {
      const term = state.terms.find((t) => t.id === action.payload.id);
      if (term) {
        term.key = action.payload.key;
      }
    },
    updateTermOp: (state, action: PayloadAction<{ id: string; op: QueryInputTermOp }>) => {
      const term = state.terms.find((t) => t.id === action.payload.id);
      if (term) {
        term.op = action.payload.op;
      }
    },
    updateTermValue: (state, action: PayloadAction<{ id: string; value: string }>) => {
      const term = state.terms.find((t) => t.id === action.payload.id);
      if (term) {
        term.value = action.payload.value;
      }
    },
    updateTermType: (state, action: PayloadAction<{ id: string; termType: "property" | "rel" | "tag" }>) => {
      const term = state.terms.find((t) => t.id === action.payload.id);
      if (term) {
        term.termType = action.payload.termType;
      }
    },
    clear: () => {
      return getDefaultQueryInput();
    },
  },
});

export const {
  setMatch,
  setSortBy,
  setSortOrder,
  addTerm,
  removeTerm,
  updateTerm,
  updateTermKey,
  updateTermOp,
  updateTermValue,
  updateTermType,
  clear,
} = queryInputSlice.actions;

export const queryInputReducer = queryInputSlice.reducer;

export const selectQuerySpec = (state: { querySpec: QueryInput }) => state.querySpec;

export const queryInputToApiFormat = (input: QueryInput) => {
  const result: any = {
    match: input.match,
  };

  if (input.terms && input.terms.length > 0) {
    result.terms = input.terms
      .filter((term) => {
        // Filter out terms with empty keys
        if (!term.key) return false;

        // Check if operator requires a value
        if (!OPERATORS_REQUIRING_TWO_VALUES.includes(term.op)) {
          return true;
        }
        return term.value !== undefined && term.value !== "";
      })
      .map((term) => {
        // Convert QueryInputTerm to API format
        const apiTerm: any = {
          op: term.op,
        };

        // Set the appropriate field based on termType
        if (term.termType === "property") {
          apiTerm.property = term.key;
        } else if (term.termType === "rel") {
          apiTerm.rel = term.key;
        } else if (term.termType === "tag") {
          apiTerm.tag = term.key;
        }

        // Add value if present
        if (term.value) {
          apiTerm.value = term.value;
        }

        return apiTerm;
      });
  }

  // Only include sort if it's enabled and has a valid 'by' field
  if (input.sort?.isEnabled && input.sort.by) {
    result.sort = {
      by: input.sort.by,
      order: input.sort.order,
    };
  }

  return result;
};
